/**
 * Device identity for auth sessions (ported from cpanel
 * `src/shared/lib/deviceIdentity.ts` — logic 1:1, UI-free).
 *
 * The auth service takes `device_id`, `device_name` and `platform` on login
 * so the backend can tell a web session apart from a mobile one. `platform`
 * is always `web` here; `device_id` is a UUID generated once per browser and
 * persisted, so re-logins on the same browser reuse the same session row
 * instead of piling up new ones (it names a row on the sessions screen).
 */

const DEVICE_ID_STORAGE_KEY = 'device_id';

export const WEB_PLATFORM = 'web';

/** UUID v4. `crypto.randomUUID` where available, RFC-4122 fallback otherwise. */
export function generateDeviceId(): string {
  const cryptoRef = globalThis.crypto;

  if (typeof cryptoRef?.randomUUID === 'function') {
    return cryptoRef.randomUUID();
  }

  // Fallback for non-secure contexts (http://) where randomUUID is undefined.
  if (typeof cryptoRef?.getRandomValues === 'function') {
    const bytes = cryptoRef.getRandomValues(new Uint8Array(16));
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40; // version 4
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * The browser's stable device id, created on first call and persisted in
 * localStorage. Falls back to a throwaway id if storage is unavailable
 * (private mode / blocked cookies) — a login must never fail over this.
 */
export function getDeviceId(): string {
  try {
    const stored = localStorage.getItem(DEVICE_ID_STORAGE_KEY)?.trim();

    if (stored) {
      return stored;
    }

    const generated = generateDeviceId();
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, generated);
    return generated;
  } catch {
    return generateDeviceId();
  }
}

interface UserAgentDataLike {
  brands?: { brand: string; version: string }[];
  platform?: string;
}

/** Vendor/model markers we can recognise from a plain user-agent string. */
const UA_DEVICE_MARKERS: [RegExp, string][] = [
  [/\bLenovo\b/i, 'Lenovo'],
  [/\bHP\b|\bHewlett-Packard\b/i, 'HP'],
  [/\bDell\b/i, 'Dell'],
  [/\bASUS\b/i, 'ASUS'],
  [/\bAcer\b/i, 'Acer'],
  [/\bSamsung\b|\bSM-[A-Z0-9]+/i, 'Samsung'],
  [/\bHuawei\b/i, 'Huawei'],
  [/\bXiaomi\b|\bRedmi\b|\bPOCO\b/i, 'Xiaomi'],
  [/\bMacintosh\b|\bMac OS X\b/i, 'Mac'],
  [/\biPhone\b/i, 'iPhone'],
  [/\biPad\b/i, 'iPad'],
  [/\bAndroid\b/i, 'Android'],
  [/\bWindows\b/i, 'Windows PC'],
  [/\bCrOS\b/i, 'Chromebook'],
  [/\bLinux\b/i, 'Linux PC'],
];

/** Browser name, used to qualify the device ("Windows PC · Chrome"). */
function detectBrowser(userAgent: string, uaData?: UserAgentDataLike): string {
  const brand = uaData?.brands?.find(
    ({ brand }) => !/Not.?A.?Brand|Chromium/i.test(brand),
  )?.brand;

  if (brand) {
    return brand;
  }

  if (/\bEdg\//.test(userAgent)) return 'Edge';
  if (/\bOPR\/|\bOpera\//.test(userAgent)) return 'Opera';
  if (/\bFirefox\//.test(userAgent)) return 'Firefox';
  if (/\bChrome\//.test(userAgent)) return 'Chrome';
  if (/\bSafari\//.test(userAgent)) return 'Safari';

  return '';
}

/**
 * Best-effort human-readable device name — "Lenovo", "HP", "Windows PC · Chrome".
 * Modern browsers hide the vendor/model in the UA, so this degrades to the OS
 * (and, when known, the browser) rather than inventing a model.
 */
export function getDeviceName(): string {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;

  if (!nav) {
    return 'Web';
  }

  const userAgent = nav.userAgent ?? '';
  const uaData = (nav as Navigator & { userAgentData?: UserAgentDataLike }).userAgentData;

  const marker = UA_DEVICE_MARKERS.find(([pattern]) => pattern.test(userAgent))?.[1];
  // `navigator.platform` is deprecated and empty in some engines — never let an
  // empty string reach the request, the field is meant to be human-readable.
  const rawDevice = marker ?? uaData?.platform ?? nav.platform ?? '';
  const device = rawDevice.trim() ? rawDevice : 'Web';
  const browser = detectBrowser(userAgent, uaData);

  return browser ? `${device} · ${browser}` : device;
}
